import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSearch, FiUsers } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { extractEntity, extractList } from '../utilities/tournamentsApi';
import {
  getPlayerPhotoUrl,
  getTeamPhotoUrl,
  isPlayerCaptain,
  isPlayerProfessional,
  playerCardPath,
  playerDisplayName,
} from '../utilities/playersApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/applicationsManagement.css';

export default function TeamRosters() {
  const { id: teamIdParam, application_id: applicationIdParam } = useParams();
  const teamId = Number(teamIdParam);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();

  const [team, setTeam] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState(String(applicationIdParam || ''));
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState({ key: 'family', dir: 'asc' });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadRoster();
  }, [teamId]);

  useEffect(() => {
    if (applicationIdParam) setSelectedApplicationId(String(applicationIdParam));
  }, [applicationIdParam]);

  const loadRoster = async () => {
    if (!teamId) {
      setLoading(false);
      setMessage({ text: t('capRosterLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const [teamResponse, appsResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getCatalogTeam, { id: teamId }),
        authorizedPost(dispatch, API_URLS.getAdminTeamApplications, { id: teamId }),
      ]);
      const nextTeam = extractEntity(teamResponse.data);
      const payload = appsResponse.data || {};
      const nextApplications = extractList(payload.applications);
      setTeam(nextTeam);
      setApplications(nextApplications);

      const requestedId = String(applicationIdParam || selectedApplicationId || '');
      const hasRequested = nextApplications.some((item) => String(item.application_id || item.id) === requestedId);
      const fallbackId = String(nextApplications[0]?.application_id || nextApplications[0]?.id || '');
      const nextId = hasRequested ? requestedId : fallbackId;
      setSelectedApplicationId(nextId);
      if (nextId && nextId !== String(applicationIdParam || '')) {
        history.replace(`/team_rosters/${teamId}/${nextId}`);
      }
    } catch (error) {
      setTeam(null);
      setApplications([]);
      setMessage({ text: getApiErrorMessage(error, t('capRosterLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const selectedApplication = applications.find((item) => (
    String(item.application_id || item.id) === String(selectedApplicationId)
  ));
  const players = extractList(selectedApplication?.players);

  const visiblePlayers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = players.filter((item) => {
      if (!query) return true;
      const haystack = [
        playerDisplayName(item),
        item.family,
        item.name,
        item.father_name,
        item.number,
        item.position,
        item.short_position,
      ].filter((value) => value != null && value !== '').join(' ').toLowerCase();
      return query.split(/\s+/).every((token) => haystack.includes(token));
    });

    return filtered.slice().sort((left, right) => {
      const dir = sort.dir === 'desc' ? -1 : 1;
      if (sort.key === 'number') {
        const leftNumber = Number(left.number);
        const rightNumber = Number(right.number);
        const leftValue = Number.isFinite(leftNumber) && leftNumber > 0 ? leftNumber : Number.POSITIVE_INFINITY;
        const rightValue = Number.isFinite(rightNumber) && rightNumber > 0 ? rightNumber : Number.POSITIVE_INFINITY;
        return (leftValue - rightValue) * dir;
      }
      if (sort.key === 'position') {
        const leftValue = String(left.short_position || left.position || '');
        const rightValue = String(right.short_position || right.position || '');
        return leftValue.localeCompare(rightValue, 'ru', { sensitivity: 'base' }) * dir;
      }
      if (sort.key === 'name') {
        return String(left.name || '').localeCompare(String(right.name || ''), 'ru', { sensitivity: 'base' }) * dir;
      }
      return playerDisplayName(left).localeCompare(playerDisplayName(right), 'ru', { sensitivity: 'base' }) * dir;
    });
  }, [players, searchQuery, sort]);

  const changeApplication = (nextId) => {
    setSelectedApplicationId(nextId);
    setSearchQuery('');
    if (nextId) history.replace(`/team_rosters/${teamId}/${nextId}`);
  };

  const columns = [
    {
      key: 'family',
      label: t('epmFamily'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-name">
          <img
            src={getPlayerPhotoUrl(item.image) || defaultPlayerImage}
            alt=""
            className="tr-contact-photo"
            onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
          />
          <span>{item.family || '—'}</span>
          {isPlayerProfessional(item) ? (
            <span className="cap-pro-badge" title={t('plStatusPro')}>{t('plStatusProShort')}</span>
          ) : null}
          {isPlayerCaptain(item) ? <span className="st-badge cap-badge accepted">{t('plCaptain')}</span> : null}
        </span>
      ),
    },
    {
      key: 'name',
      label: t('epmName'),
      className: 'st-cell-muted cap-roster-given',
      render: (item) => item.name || '—',
    },
    {
      key: 'father_name',
      label: t('epmFather'),
      className: 'st-cell-muted cap-roster-father',
      render: (item) => item.father_name || '—',
    },
    {
      key: 'position',
      label: t('mtPosition'),
      className: 'st-cell-muted cap-roster-pos',
      render: (item) => item.short_position || item.position || '—',
    },
    {
      key: 'number',
      label: t('tmNumber'),
      className: 'st-cell-priority',
      render: (item) => item.number || '—',
    },
  ];

  const teamName = team?.name || team?.team_name || t('tmUntitled');

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[
              { label: t('navApplications'), to: '/applications_managment' },
              { label: teamName, to: `/teams_management/${teamId}` },
              { label: t('capRoster') },
            ]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('tmPlayerSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="cap-toolbar-row">
            <select
              className="st-select cap-league-select"
              value={selectedApplicationId}
              disabled={!applications.length}
              onChange={(event) => changeApplication(event.target.value)}
            >
              {applications.length ? applications.map((item) => {
                const appId = item.application_id || item.id;
                return (
                  <option key={appId} value={String(appId)}>
                    {t('mtAppPlayers', {
                      name: item.application_name || item.name || t('mtAppFallback', { id: appId }),
                      count: extractList(item.players).length,
                    })}
                  </option>
                );
              }) : (
                <option value="">{t('mtNoApps')}</option>
              )}
            </select>
            <div className="st-meta">
              <strong>{t('stFound', { count: visiblePlayers.length })}</strong>
            </div>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        {loading ? (
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('capRosterLoading')}</p>
          </div>
        ) : (
          <div className="st-scroll">
            <div className="st-content">
              <div className="st-block cap-roster-team">
                <div className="cap-team">
                  <img
                    src={getTeamPhotoUrl(team?.image) || defaultTeamLogo}
                    alt=""
                    className="cap-team-logo"
                    onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                  />
                  <div>
                    <Link to={`/teams_management/${teamId}`} className="cap-team-name">
                      {teamName}
                    </Link>
                    <div className="cap-captain">
                      {selectedApplication
                        ? t('mtAppPlayers', {
                          name: selectedApplication.application_name || selectedApplication.name || t('capRoster'),
                          count: players.length,
                        })
                        : t('mtPickAppHint')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="st-block">
                <h2 className="st-block-header">{t('capRoster')}</h2>
                <ReorderableTable
                  tableClassName="st-table-roster"
                  columns={columns}
                  items={visiblePlayers}
                  getRowKey={(item, index) => item.id || index}
                  reorderMode={false}
                  sort={sort}
                  onSort={setSort}
                  getRowHref={(item) => (item.id ? playerCardPath(item.id, teamId) : null)}
                  empty={(
                    <div className="st-empty">
                      <FiUsers size={48} />
                      <h3>{searchQuery ? t('tmNoPlayerSearch') : t('capRosterEmpty')}</h3>
                      <p>{searchQuery ? t('capRosterEmptySearchHint') : t('capRosterEmptyHint')}</p>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
